/**
 * El informe de integridad de la agenda (spec 045).
 *
 * Solo lectura. Un hallazgo se mira en el calendario; aquí no hay
 * botón de arreglar.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from 'react-router'

import { useSoloLectura } from '../../auth'
import { api } from '../../lib/api'
import { fechaHora } from '../../lib/tiempo'

type Tipo =
    | 'hueco_doble'
    | 'intervalo_pisado'
    | 'persona_solapada'
    | 'telefono_duplicado'
    | 'cita_en_ausencia'

interface Hallazgo {
    tipo: Tipo
    citaIds: number[]
    profesionalId?: number
    clienteIds?: number[]
    inicio?: string
}

interface Informe {
    id: number
    accountId: number
    generadoEn: string
    cuantos: number
    hallazgos: Hallazgo[]
    error: string | null
    repara: false
}

interface Respuesta {
    accountId: number
    repara: false
    informe: Informe | null
}

const ROTULO: Record<Tipo, string> = {
    hueco_doble: 'Dos citas a la misma hora, mismo profesional',
    intervalo_pisado: 'Dos citas se pisan en la misma columna',
    persona_solapada: 'La misma persona en dos sitios a la vez',
    telefono_duplicado: 'Dos fichas con el mismo teléfono',
    cita_en_ausencia: 'Cita encima de un día anulado',
}

export function SupervisorNegocio() {
    const { accountId } = useParams<{ accountId: string }>()
    const qc = useQueryClient()
    const soloLectura = useSoloLectura()

    const { data, isPending, isError } = useQuery<Respuesta>({
        queryKey: ['supervisor', accountId],
        queryFn: () => api.get<Respuesta>(`/business/${accountId}/supervisor`),
        enabled: Boolean(accountId),
    })

    const correr = useMutation({
        mutationFn: () => api.post<Informe>(`/business/${accountId}/supervisor/correr`),
        onSuccess: () => void qc.invalidateQueries({ queryKey: ['supervisor', accountId] }),
    })

    const informe = data?.informe ?? (correr.data ?? null)

    return (
        <div className="mx-auto max-w-3xl px-4 py-6 md:px-6">
            <h1 className="text-xl font-semibold text-neutral-100">Supervisor de la agenda</h1>
            <p className="mt-2 text-sm text-neutral-400">
                Mira si el calendario se contradice. <strong className="font-medium text-neutral-200">No repara</strong>
                : un hallazgo se abre en el calendario y se decide a mano. Corre cada noche; también se puede lanzar
                ahora.
            </p>

            <div className="mt-4 flex flex-wrap items-center gap-3">
                {!soloLectura && (
                <button
                    type="button"
                    onClick={() => correr.mutate()}
                    disabled={correr.isPending}
                    className="inline-flex min-h-11 items-center rounded-md bg-sky-500/20 px-3 text-sm text-sky-100 hover:bg-sky-500/30 disabled:opacity-50"
                >
                    {correr.isPending ? 'Mirando…' : 'Mirar ahora'}
                </button>
                )}
                {informe && (
                    <span className="text-sm text-neutral-500">
                        Última vez {fechaHora(informe.generadoEn)}
                    </span>
                )}
            </div>

            {isPending && <p className="mt-6 text-sm text-neutral-500">Cargando…</p>}
            {isError && (
                <p className="mt-6 text-sm text-red-300">No se pudo leer el informe.</p>
            )}
            {correr.isError && (
                <p className="mt-4 text-sm text-red-300">No se pudo mirar ahora.</p>
            )}

            {informe?.error && (
                <p className="mt-6 text-sm text-amber-300">
                    No se pudo leer la agenda ({informe.error}). No se afirma que esté limpia.
                </p>
            )}

            {informe && !informe.error && informe.cuantos === 0 && (
                <p className="mt-6 text-sm text-neutral-300">Nada raro en las citas futuras.</p>
            )}

            {informe && informe.cuantos > 0 && (
                <ul className="mt-6 space-y-3">
                    {informe.hallazgos.map((h, i) => (
                        <li
                            key={`${h.tipo}-${h.citaIds.join('-')}-${i}`}
                            className="rounded-md border border-neutral-800 bg-neutral-900/40 px-3 py-3 text-sm text-neutral-200"
                        >
                            <p className="font-medium">{ROTULO[h.tipo]}</p>
                            <p className="mt-1 text-neutral-400">
                                {h.citaIds.length > 0 && (
                                    <>
                                        Citas {h.citaIds.join(', ')}
                                        {h.inicio ? ` · ${h.inicio}` : ''}
                                        {h.profesionalId ? ` · profesional ${h.profesionalId}` : ''}
                                    </>
                                )}
                                {h.clienteIds && h.clienteIds.length > 0 && (
                                    <>
                                        {h.citaIds.length > 0 ? ' · ' : ''}
                                        Clientes {h.clienteIds.join(', ')}
                                    </>
                                )}
                            </p>
                            <Link
                                to={`/business/${accountId}/calendar`}
                                className="mt-2 inline-block text-sky-300 hover:text-sky-200"
                            >
                                Abrir calendario
                            </Link>
                        </li>
                    ))}
                </ul>
            )}

            {!isPending && !informe && !isError && (
                <p className="mt-6 text-sm text-neutral-500">
                    Aún no ha corrido. «Mirar ahora» o esperar a las 04:00 UTC.
                </p>
            )}
        </div>
    )
}
